import type { Tone } from '@/design-system';

import type {
    BillingIntegrationResource,
    BillingSummaryResource,
    InspectionTypeResource,
    InvoiceBatchResource,
    InvoicePdfLinkResource,
    InvoiceResource,
    OrderInvoiceResource,
    TariffResource,
} from './types';

/**
 * Facturatie en tarieven (scherm 10), zoals de schermen ze zien.
 *
 * De `to*`-functies zijn de enige plek waar de lijnvorm uit `types.ts` wordt
 * vertaald. Bedragen blijven strings met twee decimalen; `money.ts` maakt er
 * tekst van zonder ze ooit naar `number` om te zetten.
 */

/* -------------------------------------------------------------------------- */
/* Facturen                                                                    */
/* -------------------------------------------------------------------------- */

export const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'overdue', 'credited'] as const;

export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * Gelijk aan `InvoiceStatus::label()` in de API. Een factuur draagt haar eigen
 * `status_label`; deze lijst is er voor het filter, dat er al is voor de data.
 */
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
    draft: 'Concept',
    sent: 'Verstuurd',
    paid: 'Betaald',
    overdue: 'Te laat',
    credited: 'Gecrediteerd',
};

/**
 * Vier tonen, geen vijfde (principe 9). Concept en gecrediteerd zijn neutraal,
 * verstuurd en betaald groen, te laat het luide rood (`alarm`): er is iets
 * achterstallig - niet het zachte rood van een gebrek.
 */
export const INVOICE_STATUS_TONES: Record<InvoiceStatus, Tone> = {
    draft: 'neutral',
    sent: 'accent',
    paid: 'accent',
    overdue: 'alarm',
    credited: 'neutral',
};

/**
 * `InvoiceStatus::allowedTransitions()` uit de API. Een knop verschijnt alleen
 * als de API die stap toestaat; anders zou hij altijd een 409 opleveren.
 */
const INVOICE_TRANSITIONS: Record<InvoiceStatus, readonly InvoiceStatus[]> = {
    draft: ['sent'],
    sent: ['paid', 'overdue', 'credited'],
    overdue: ['paid', 'credited'],
    paid: ['credited'],
    credited: [],
};

/**
 * Eén regel van een factuur. Zolang de factuur een concept is, is elk veld hier
 * nog van jou: omschrijving, aantal, bedrag en het btw-tarief van die ene regel.
 */
export interface InvoiceLine {
    description: string;
    /** `"1.00"`, `"1.50"` - altijd twee decimalen. */
    quantity: string;
    /** Per stuk, exclusief btw. */
    unitPrice: string;
    /** Percentage: `"21.00"`. */
    vatRate: string;
    /** Aantal maal stukprijs, exclusief btw. De API rekent dit uit, nooit het scherm. */
    total: string;
    vatAmount: string;
    /** De dienst van de keuring die deze regel factureert; `null` bij een losse regel. */
    serviceName: string | null;
    sortOrder: number;
}

/** De btw per tarief onder een factuur: wat een klant met een rekenmachine natelt. */
export interface VatBreakdownRow {
    vatRate: string;
    /** Het bedrag waarover dit tarief gaat, exclusief btw. */
    base: string;
    vat: string;
}

export interface Invoice {
    uuid: string;
    /** `null` voor een concept: het nummer komt pas bij versturen. */
    number: string | null;
    /** Wat je in een lijst toont: het nummer, of `concept 4F2K9Q`. */
    label: string;
    /** `null` voor een status die de app niet kent; zo'n factuur krijgt geen acties. */
    status: InvoiceStatus | null;
    statusLabel: string;
    /** Wie de factuur krijgt; nodig om er een nieuwe factuur voor op te stellen. */
    clientUuid: string | null;
    clientName: string | null;
    orderUuid: string | null;
    orderNumber: string | null;
    /** Het adres van het object van de keuring, als de factuur bij een keuring hoort. */
    address: string | null;
    reference: string | null;
    notes: string | null;
    paymentTermDays: number;
    /** Exclusief btw. */
    subtotal: string;
    vatAmount: string;
    /** Inclusief btw. */
    total: string;
    issuedAt: string | null;
    dueAt: string | null;
    paidAt: string | null;
    hasPdf: boolean;
    /** `null` in een lijst: die stuurt de regels niet mee. `GET /invoices/{uuid}` wel. */
    lines: InvoiceLine[] | null;
    vatBreakdown: VatBreakdownRow[] | null;
}

function invoiceStatusOf(value: string): InvoiceStatus | null {
    return (INVOICE_STATUSES as readonly string[]).includes(value)
        ? (value as InvoiceStatus)
        : null;
}

export function toInvoice(resource: InvoiceResource): Invoice {
    return {
        uuid: resource.uuid,
        number: resource.number,
        label: resource.label,
        status: invoiceStatusOf(resource.status),
        statusLabel: resource.status_label,
        clientUuid: resource.client_uuid,
        clientName: resource.client_name,
        orderUuid: resource.order_uuid,
        orderNumber: resource.order?.number ?? null,
        address: resource.order?.address ?? null,
        reference: resource.reference,
        notes: resource.notes,
        paymentTermDays: resource.payment_term_days,
        subtotal: resource.subtotal,
        vatAmount: resource.vat_amount,
        total: resource.total,
        issuedAt: resource.issued_at,
        dueAt: resource.due_at,
        paidAt: resource.paid_at,
        hasPdf: resource.has_pdf,
        lines:
            resource.lines?.map((line) => ({
                description: line.description,
                quantity: line.quantity,
                unitPrice: line.unit_price,
                vatRate: line.vat_rate,
                total: line.total,
                vatAmount: line.vat_amount,
                serviceName: line.inspection_type?.name ?? null,
                sortOrder: line.sort_order,
            })) ?? null,
        vatBreakdown:
            resource.vat_breakdown?.map((row) => ({
                vatRate: row.vat_rate,
                base: row.base,
                vat: row.vat,
            })) ?? null,
    };
}

/**
 * Mag je deze factuur nog wijzigen?
 *
 * Alleen een concept. Een verstuurde factuur is een vastgelegd document met een
 * nummer in een doorlopende reeks; die corrigeer je met een creditering en een
 * nieuwe factuur, niet door het origineel te herschrijven. De API weigert het
 * ook (409 `invoice-not-editable`); dit houdt een knop weg die altijd zou falen.
 */
export function isInvoiceEditable(invoice: Pick<Invoice, 'status'>): boolean {
    return invoice.status === 'draft';
}

/** Eén regel zoals het scherm hem bewerkt: de drie getallen en de tekst, meer niet. */
export interface InvoiceLineDraft {
    description: string;
    /** `"1.00"` */
    quantity: string;
    /** Exclusief btw. */
    unitPrice: string;
    /** Percentage: `"21.00"`. */
    vatRate: string;
}

/**
 * Wat je aan een concept wijzigt. Alles is optioneel: wat je niet meestuurt
 * blijft staan. `lines` is de uitzondering - die zijn de hele factuur, want een
 * samenvoeging zou een weggehaalde regel niet kunnen onderscheiden van een regel
 * die je niet meestuurde.
 */
export interface InvoiceDraftChanges {
    reference?: string | null;
    notes?: string | null;
    paymentTermDays?: number;
    lines?: readonly InvoiceLineDraft[];
}

/**
 * Een nieuwe factuur opstellen: voor een klant, eventueel bij een keuring, met
 * regels die je zelf meegeeft. Zo maak je ook de herstelfactuur na een
 * creditering - dezelfde regels, en daarna bewerk je wat er anders moet.
 */
export interface NewInvoice {
    clientUuid: string;
    orderUuid?: string | null;
    reference?: string | null;
    lines: readonly InvoiceLineDraft[];
}

/** Mag deze factuur van haar huidige status naar `target`? */
export function canMoveInvoiceTo(invoice: Pick<Invoice, 'status'>, target: InvoiceStatus): boolean {
    return invoice.status !== null && INVOICE_TRANSITIONS[invoice.status].includes(target);
}

/** De statussen die `POST /invoices/{invoice}/status` accepteert; `sent` gaat via versturen. */
export type InvoiceStatusChange = Extract<InvoiceStatus, 'paid' | 'overdue' | 'credited'>;

export interface InvoiceFilters {
    status?: InvoiceStatus | '';
    /** Verstuurd of te laat: wat nog betaald moet worden. */
    outstanding?: boolean;
    /** Een deel van het factuurnummer. */
    number?: string;
    page?: number;
    perPage?: number;
}

/* -------------------------------------------------------------------------- */
/* Versturen in één actie                                                      */
/* -------------------------------------------------------------------------- */

export const INVOICE_BATCH_RESULTS = ['sent', 'failed', 'forbidden', 'not_found'] as const;

export type InvoiceBatchResult = (typeof INVOICE_BATCH_RESULTS)[number];

export interface InvoiceBatchLine {
    invoiceUuid: string;
    /** Het nummer dat de factuur kreeg; `null` als ze niet verstuurd is. */
    number: string | null;
    result: InvoiceBatchResult;
    /** De reden in gewone taal, als het niet lukte. */
    problem: string | null;
}

/**
 * Eén actie, een uitkomst per factuur. Een batch die halverwege strandt is niet
 * "gelukt" en niet "mislukt": de gebruiker ziet welke factuur bleef liggen en
 * waarom.
 */
export interface InvoiceBatchOutcome {
    requested: number;
    sent: number;
    notSent: number;
    lines: InvoiceBatchLine[];
}

function batchResultOf(value: string): InvoiceBatchResult {
    return (INVOICE_BATCH_RESULTS as readonly string[]).includes(value)
        ? (value as InvoiceBatchResult)
        : 'failed';
}

export function toInvoiceBatchOutcome(resource: InvoiceBatchResource): InvoiceBatchOutcome {
    return {
        requested: resource.results.length,
        sent: resource.sent,
        notSent: resource.failed,
        lines: resource.results.map((item) => ({
            invoiceUuid: item.uuid,
            number: item.number,
            result: batchResultOf(item.result),
            problem: item.problem,
        })),
    };
}

/* -------------------------------------------------------------------------- */
/* De factuur bij het rapport                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Waar de factuur van een keuring staat, gezien vanaf het rapport dat de deur uit gaat
 * (`SendableInvoiceState` in de API).
 */
export const ORDER_INVOICE_STATES = [
    'none',
    'nothing_to_invoice',
    'draft',
    'rendering',
    'ready',
    'delivered',
    'closed',
] as const;

export type OrderInvoiceState = (typeof ORDER_INVOICE_STATES)[number];

function isOrderInvoiceState(value: string): value is OrderInvoiceState {
    return (ORDER_INVOICE_STATES as readonly string[]).includes(value);
}

/**
 * Wat "Factuur meesturen" voor deze keuring doet. `enclosable`: de factuur kan mee.
 * Versturen maakt dan de factuur uit de diensten van de keuring (`none`), geeft een
 * concept een nummer (`draft`) of neemt een verstuurde factuur zoals ze is
 * (`rendering`, `ready`). Een factuur die de klant al heeft (`delivered`) gaat niet
 * nog een keer mee.
 */
export interface OrderInvoice {
    state: OrderInvoiceState;
    stateLabel: string;
    enclosable: boolean;
    /** De zin van de API bij deze stand; `null` als er niets bij hoeft. */
    reason: string | null;
    invoiceUuid: string | null;
    /** `null` voor een concept: het nummer komt bij versturen. */
    number: string | null;
    /** Het nummer, of `concept 4F2K9Q`. */
    label: string | null;
    total: string | null;
    dueAt: string | null;
    /** Wanneer de klant deze factuur al kreeg, los of met het rapport. */
    mailedAt: string | null;
}

export function toOrderInvoice(resource: OrderInvoiceResource): OrderInvoice {
    const state = isOrderInvoiceState(resource.state) ? resource.state : 'closed';

    return {
        state,
        stateLabel: resource.state_label,
        /* Een stand die deze app nog niet kent, biedt hij niet aan. */
        enclosable: resource.enclosable && isOrderInvoiceState(resource.state),
        reason: resource.reason,
        invoiceUuid: resource.invoice_uuid,
        number: resource.number,
        label: resource.label,
        total: resource.total,
        dueAt: resource.due_at,
        mailedAt: resource.mailed_at,
    };
}

/* -------------------------------------------------------------------------- */
/* PDF                                                                         */
/* -------------------------------------------------------------------------- */

/** Een tijdelijke, ondertekende link: direct openen, niet bewaren. */
export interface InvoicePdfLink {
    url: string;
    filename: string;
    expiresInSeconds: number;
}

export function toInvoicePdfLink(resource: InvoicePdfLinkResource): InvoicePdfLink {
    return {
        url: resource.url,
        filename: resource.filename,
        expiresInSeconds: resource.url_expires_in,
    };
}

/* -------------------------------------------------------------------------- */
/* Tarieven                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Wat het bedrijf rekent voor een dienst en wat de inspecteur ervoor krijgt.
 * Een bedrag verandert nooit: een nieuwe prijs is een nieuw tarief met een
 * ingangsdatum, en het lopende tarief stopt de dag ervoor.
 */
export interface Tariff {
    uuid: string;
    serviceUuid: string | null;
    serviceName: string | null;
    name: string;
    price: string;
    inspectorFee: string;
    margin: string;
    vatRate: string;
    /** `2026-09-16` */
    validFrom: string;
    validUntil: string | null;
}

export function toTariff(resource: TariffResource): Tariff {
    return {
        uuid: resource.uuid,
        serviceUuid: resource.inspection_type_uuid,
        serviceName: resource.inspection_type?.name ?? null,
        name: resource.name,
        price: resource.price,
        inspectorFee: resource.inspector_fee,
        margin: resource.margin,
        vatRate: resource.vat_rate,
        validFrom: resource.valid_from,
        validUntil: resource.valid_until,
    };
}

/** Geldt dit tarief vandaag of later nog? Datums als `YYYY-MM-DD`. */
export function isTariffInForce(tariff: Pick<Tariff, 'validUntil'>, today: string): boolean {
    return tariff.validUntil === null || tariff.validUntil >= today;
}

/** Een dienst waar een tarief voor kan gelden (`GET /inspection-types`). */
export interface TariffService {
    uuid: string;
    name: string;
}

export function toTariffServices(resources: readonly InspectionTypeResource[]): TariffService[] {
    return [...resources]
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'nl'))
        .map((resource) => ({ uuid: resource.uuid, name: resource.name }));
}

export interface NewTariff {
    serviceUuid: string;
    name: string;
    price: string;
    inspectorFee: string;
    vatRate: string;
    /** Leeg is vandaag. */
    validFrom: string | null;
}

/** `PATCH /tariffs/{tariff}` kent alleen de naam en de einddatum. */
export interface TariffChanges {
    name?: string;
    validUntil?: string | null;
}

/* -------------------------------------------------------------------------- */
/* De maand en de boekhouding                                                  */
/* -------------------------------------------------------------------------- */

/** De donkere maandkaart: uitgegeven facturen van één maand, zonder concepten en creditering. */
export interface MonthSummary {
    /** `2026-09` */
    period: string;
    periodLabel: string;
    invoiceCount: number;
    /** Exclusief btw. */
    invoiced: string;
    inspectorFees: string;
    margin: string;
    /** Inclusief btw, nog niet betaald. */
    outstanding: string;
    overdue: string;
    overdueCount: number;
}

export function toMonthSummary(resource: BillingSummaryResource): MonthSummary {
    return {
        period: resource.period,
        periodLabel: resource.period_label,
        invoiceCount: resource.invoice_count,
        invoiced: resource.invoiced,
        inspectorFees: resource.inspector_fees,
        margin: resource.margin,
        outstanding: resource.outstanding,
        overdue: resource.overdue,
        overdueCount: resource.overdue_count,
    };
}

export interface BillingIntegration {
    connected: boolean;
    /** "Niet gekoppeld" - uit de API, niet verzonnen. */
    statusLabel: string;
    /** "Exact Online"; `null` zolang er geen pakket gekoppeld is. */
    providerLabel: string | null;
    lastSyncedAt: string | null;
    /** De zin die erbij hoort: "Er is nog geen boekhoudpakket gekoppeld. …" */
    message: string;
}

export function toBillingIntegration(resource: BillingIntegrationResource): BillingIntegration {
    return {
        connected: resource.connected,
        statusLabel: resource.status_label,
        providerLabel: resource.provider_label,
        lastSyncedAt: resource.last_synced_at,
        message: resource.message,
    };
}
